import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Languages } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { fetchCountriesOverlay, ovText, GEO_STR as S, type CountryOverlayRow } from './geoApi'

/**
 * A11.09 — Til va mintaqa sozlamalari. REAL `/admin/countries` + greenfield
 * overlay (`/admin/geo/locale` → `{code, locale, timezone}`): davlat bo'yicha
 * standart til/vaqt-mintaqa. Read-only (blok emas — bu sozlama ekrani).
 */
export function LocaleSettingsScreen() {
  const columns = useMemo<AdminColumn<CountryOverlayRow>[]>(
    () => [
      {
        key: 'flag',
        header: '',
        type: 'text',
        width: 44,
        sortable: false,
        filterable: false,
        render: (r) => <span className="text-base">{r.flag || '🌐'}</span>,
      },
      { key: 'code', header: S.code, type: 'id', width: 80 },
      {
        key: 'name',
        header: S.country,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/geo/${r.code}`} className="text-accent hover:underline">
            {r.name}
          </Link>
        ),
      },
      { key: 'dial_code', header: S.prefix, type: 'text', width: 90 },
      {
        key: 'locale',
        header: S.locale,
        type: 'text',
        width: 150,
        sortable: false,
        accessor: (r) => ovText(r, 'locale'),
        render: (r) => {
          const v = ovText(r, 'locale')
          return v ? <span className="font-mono text-primary">{v}</span> : <span className="text-tertiary">{S.none}</span>
        },
      },
      {
        key: 'timezone',
        header: S.timezone,
        type: 'text',
        minWidth: 160,
        sortable: false,
        accessor: (r) => ovText(r, 'timezone'),
        render: (r) => {
          const v = ovText(r, 'timezone')
          return v ? <span className="text-primary">{v}</span> : <span className="text-tertiary">{S.none}</span>
        },
      },
    ],
    [],
  )

  const fetcher = useCallback(fetchCountriesOverlay('/admin/geo/locale', 'locales'), [])

  return (
    <>
      <PageHeader
        title={S.localeTitle}
        screenId="A11.09"
        description={S.localeNote}
        actions={
          <Link
            to="/geo"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.backToList}
          </Link>
        }
      />
      <DataTable<CountryOverlayRow>
        screenId="A11.09"
        permission={PERMISSIONS.COUNTRIES_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.code}
        exportable
        emptyState={{ icon: <Languages size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
